import { Button } from "@/components/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, LoaderCircle } from "lucide-react";
import type {
  AiChatQuestionAnswer,
  AiChatUserQuestion,
} from "@/lib/types/ai-chat";
import type { ChatTimelineItemProps } from "./ChatTimelineItem";
import { parseUserQuestion } from "./chat-user-question";

function savedDraft(key: string): Partial<Record<string, string>> {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? "{}");
    return value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value).filter(
            ([, answer]) => typeof answer === "string",
          ),
        )
      : {};
  } catch {
    return {};
  }
}

function QuestionForm({
  question,
  runId,
  onAnswer,
}: {
  question: AiChatUserQuestion;
  runId: string;
  onAnswer: ChatTimelineItemProps["onAnswerQuestion"];
}) {
  const key = `lawworks-question:${question.requestId}`;
  const [draft, setDraft] = useState(() => savedDraft(key));
  const [submitted, setSubmitted] = useState<AiChatQuestionAnswer[]>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string>();
  const answered = question.status === "answered" || Boolean(submitted);
  const answers = question.answers ?? submitted;
  const ready = question.questions.every((entry) => draft[entry.id]?.trim());
  useEffect(() => {
    try {
      if (answered) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {
      /* A blocked storage area must not prevent answering. */
    }
  }, [answered, draft, key]);

  return (
    <form
      className="border-border bg-muted/20 space-y-4 rounded-xl border p-4"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!ready || !onAnswer || submitting.current || answered) return;
        submitting.current = true;
        setIsSubmitting(true);
        setError(undefined);
        const next = question.questions.map((entry) => ({
          questionId: entry.id,
          answer: draft[entry.id] ?? "",
        }));
        try {
          await onAnswer(runId, question.requestId, next);
          setSubmitted(next);
        } catch {
          setError("Your answers couldn't be saved. Try again.");
        } finally {
          submitting.current = false;
          setIsSubmitting(false);
        }
      }}
    >
      {answered && (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Check className="size-4" />
          Answered
        </p>
      )}
      {question.questions.map((entry, index) => (
        <fieldset
          key={entry.id}
          disabled={isSubmitting || answered}
          className="min-w-0 space-y-2"
        >
          <legend data-chat-search-content className="mb-2 font-medium">
            {entry.title}
          </legend>
          {answered ? (
            <p
              data-chat-search-content
              className="text-muted-foreground whitespace-pre-wrap"
            >
              {answers?.find((answer) => answer.questionId === entry.id)
                ?.answer ?? "Answer saved"}
            </p>
          ) : (
            <>
              {entry.options.map((option) => (
                <label
                  key={option}
                  className="border-border has-[:checked]:border-primary flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <input
                    type="radio"
                    name={`${question.requestId}-${entry.id}`}
                    value={option}
                    checked={draft[entry.id] === option}
                    onChange={() => {
                      setDraft((previous) => ({
                        ...previous,
                        [entry.id]: option,
                      }));
                    }}
                    className="mt-0.5"
                  />
                  <span>{option}</span>
                </label>
              ))}
              <label
                htmlFor={`${question.requestId}-${index}`}
                className="text-muted-foreground block text-xs"
              >
                {entry.options.length
                  ? "Or write your own answer"
                  : "Your answer"}
              </label>
              <textarea
                id={`${question.requestId}-${index}`}
                value={draft[entry.id] ?? ""}
                onChange={(event) => {
                  setDraft((previous) => ({
                    ...previous,
                    [entry.id]: event.target.value,
                  }));
                }}
                rows={2}
                maxLength={4000}
                className="border-input bg-background focus-visible:ring-ring w-full resize-y rounded-lg border p-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              />
            </>
          )}
        </fieldset>
      ))}
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      {!answered && (
        <Button type="submit" disabled={!ready || !onAnswer || isSubmitting}>
          {isSubmitting && <LoaderCircle className="animate-spin" />}
          {isSubmitting
            ? "Saving answers…"
            : error
              ? "Retry"
              : question.questions.length > 1
                ? "Send answers"
                : "Send answer"}
        </Button>
      )}
    </form>
  );
}

export function ChatUserQuestion({
  item,
  body,
  isBodyLoading,
  onAnswerQuestion,
  onLoadItemBody,
}: ChatTimelineItemProps) {
  const [loadError, setLoadError] = useState(false);
  const attemptedChunk = useRef<string>();
  const dataBody =
    body?.part === "data" && body.revision === item.revision ? body : undefined;
  let question = parseUserQuestion(item.payload.userQuestion);
  if (dataBody?.isComplete) {
    try {
      const data = JSON.parse(dataBody.text);
      question =
        parseUserQuestion(data.content?.userQuestion ?? data.userQuestion) ??
        question;
    } catch {
      /* Preserve the inline question if the saved body is unavailable. */
    }
  }
  const needsAnswers =
    question?.status === "answered" && !question.answers && item.hasBody;
  const loadAnswers = useCallback(async () => {
    if (!onLoadItemBody) return;
    setLoadError(false);
    try {
      await onLoadItemBody(item, "data");
    } catch {
      setLoadError(true);
    }
  }, [item, onLoadItemBody]);
  useEffect(() => {
    if (
      !needsAnswers ||
      !onLoadItemBody ||
      isBodyLoading ||
      loadError ||
      dataBody?.isComplete ||
      dataBody?.reachedClientLimit
    )
      return;
    const key = `${item.id}:${item.revision}:${dataBody?.text.length ?? 0}`;
    if (attemptedChunk.current === key) return;
    attemptedChunk.current = key;
    void loadAnswers();
  }, [
    needsAnswers,
    onLoadItemBody,
    isBodyLoading,
    loadError,
    dataBody,
    item.id,
    item.revision,
    loadAnswers,
  ]);
  if (!question || !item.runId)
    return (
      <p className="text-muted-foreground">
        This question is unavailable. Refresh to try again.
      </p>
    );
  return (
    <div>
      <QuestionForm
        key={question.requestId}
        question={question}
        runId={item.runId}
        onAnswer={onAnswerQuestion}
      />
      {needsAnswers &&
        onLoadItemBody &&
        !loadError &&
        !dataBody?.isComplete &&
        !dataBody?.reachedClientLimit && (
          <p role="status" className="text-muted-foreground mt-2 text-sm">
            Loading saved answers…
          </p>
        )}
      {needsAnswers && loadError && (
        <div className="mt-2">
          <p role="alert" className="text-muted-foreground text-sm">
            Your answers are saved, but couldn’t be loaded.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isBodyLoading}
            onClick={() => void loadAnswers()}
          >
            Retry loading answers
          </Button>
        </div>
      )}
      {needsAnswers &&
        (dataBody?.reachedClientLimit || dataBody?.isComplete) && (
          <p className="text-muted-foreground mt-2 text-sm">
            Your answers are saved, but their full text is unavailable in this
            preview.
          </p>
        )}
    </div>
  );
}
